import React, { useEffect, useId, useRef, useState } from "react";
import { ScaleLoader } from "react-spinners";
import Calendar from "react-calendar";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import MedicineSearchBar from "../MedicineSearchBar/MedicineSearchBar";
import NotificationMessage from "../UI/NotificationMessage/NotificationMessage";
import ConsultRfzoItemDialog from "./ConsultRfzoItemDialog";
import { useConsultOrderContext } from "../../contexts/ConsultOrderContext";
import { ConsultOrderPhase } from "../../hooks/useConsultOrderLifecycle";
import {
  ConsultRfzoValidationErrors,
  ConsultRfzoSubmitMode,
  isRfzoItemInStock,
  someRfzoItemInStock,
} from "../../utils/consultRfzoPayload";
import { ConsultRfzoTherapyResponse } from "../../types/consultRfzo.types";
import { ItemBasicResponse } from "../../types/medicineSearch.types";
import styles from "./ConsultRfzoFormModal.module.scss";

// ITAM-2614: the consult RFZO therapy modal. Two steps over the shared
// ConsultOrderContext rfzoDraft:
//   RFZO_FORM / RFZO_SUBMITTING -> the entry form (identity, LBO, items, dates,
//   notes; submit = POST pharmacist_create),
//   RFZO_RESULT -> the 201 outcome (orderable -> continue into the order form;
//   terminal -> view-only confirmation, the ONLY action is closing — Q6).

const FIELD_FOCUS_ORDER: (keyof ConsultRfzoValidationErrors)[] = [
  "lbo",
  "jmbg",
  "firstName",
  "secondName",
  "items",
  "lastTherapyDate",
];

const focusFirstInvalidField = (
  errors: ConsultRfzoValidationErrors,
  container: HTMLElement | null
): void => {
  const scope: ParentNode = container ?? document;
  for (const key of FIELD_FOCUS_ORDER) {
    if (!errors[key]) {
      continue;
    }
    const el = scope.querySelector<HTMLElement>(
      `[data-consult-field="${key}"]`
    );
    if (el) {
      // Some markers wrap a control rather than being one (the item search, the
      // month calendar). Focus the real control inside, so the pharmacist lands
      // on something named and visibly focused instead of a bare div.
      const control = el.matches("input, select, textarea, button")
        ? el
        : el.querySelector<HTMLElement>("input, select, textarea, button") ?? el;
      control.focus();
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }
  }
};

export interface ConsultRfzoFormModalProps {
  isOpen: boolean;
  phase: ConsultOrderPhase;
  graceActive: boolean;
  consultEnded: boolean;
  rfzoResult: ConsultRfzoTherapyResponse | null;
  /** Which submit produced the result — distinguishes the two no-content cases. */
  rfzoResultMode: ConsultRfzoSubmitMode | null;
  submitError: string | null;
  validationErrors: ConsultRfzoValidationErrors;
  /** Bumped on each failed submit attempt; drives focus to the first invalid field. */
  submitAttemptId: number;
  onSubmit: (mode: ConsultRfzoSubmitMode) => void;
  onClose: () => void;
  onCloseResult: () => void;
  onStartOrder: () => void;
  onDismissError: () => void;
}

const TITLE_ID = "consult-rfzo-form-title";

const formatAmount = (value: number): string =>
  `${value.toLocaleString("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} RSD`;

const ConsultRfzoFormModal: React.FC<ConsultRfzoFormModalProps> = ({
  isOpen,
  phase,
  graceActive,
  consultEnded,
  rfzoResult,
  rfzoResultMode,
  submitError,
  validationErrors,
  submitAttemptId,
  onSubmit,
  onClose,
  onCloseResult,
  onStartOrder,
  onDismissError,
}) => {
  const { prefill, rfzoDraft, updateRfzoDraft, addRfzoItem, removeRfzoItem } =
    useConsultOrderContext();

  const bodyRef = useRef<HTMLDivElement>(null);
  const resultPrimaryRef = useRef<HTMLButtonElement>(null);
  const resultCloseRef = useRef<HTMLButtonElement>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Identity of the LATEST copy attempt in the CURRENT open session. Every
  // attempt claims a new generation and a modal close bumps it too, so a
  // stale settle can never write feedback — not even after a quick reopen,
  // which an "is the modal open now?" check would wave through.
  const copyGenRef = useRef(0);

  const [pendingItem, setPendingItem] = useState<ItemBasicResponse | null>(
    null
  );
  const [noContentConfirm, setNoContentConfirm] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">(
    "idle"
  );

  const reactId = useId();
  const lboId = `${reactId}-lbo`;
  const jmbgId = `${reactId}-jmbg`;
  const firstNameId = `${reactId}-first-name`;
  const secondNameId = `${reactId}-second-name`;
  const phoneId = `${reactId}-phone`;
  const noteMarketplaceId = `${reactId}-note-marketplace`;
  const noteUserId = `${reactId}-note-user`;
  const lboErrId = `${lboId}-error`;
  const jmbgErrId = `${jmbgId}-error`;
  const firstNameErrId = `${firstNameId}-error`;
  const secondNameErrId = `${secondNameId}-error`;

  const callerName =
    [prefill?.user.firstName, prefill?.user.secondName]
      .filter((part) => part && part.trim())
      .join(" ")
      .trim() ||
    prefill?.user.username ||
    "korisnika";

  const isResult = phase === "RFZO_RESULT";
  const isSubmitting = phase === "RFZO_SUBMITTING";

  const userEmail = prefill?.user.username?.trim() || null;
  const emailMissing = userEmail == null;
  const prefillHadLbo = Boolean(prefill?.user.lbo?.trim());

  // On a failed submit attempt (nonce bump), move focus to the first invalid field.
  useEffect(() => {
    if (submitAttemptId <= 0) {
      return;
    }
    focusFirstInvalidField(validationErrors, bodyRef.current);
    // Re-focus once per attempt only — never while the pharmacist is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttemptId]);

  // The FORM/SUBMITTING -> RESULT swap happens with the modal already open
  // (react-modal only applies initialFocusRef on the open transition), so move
  // focus to the result's primary action here — same pattern as the order form.
  useEffect(() => {
    if (isResult) {
      (resultPrimaryRef.current ?? resultCloseRef.current)?.focus();
    }
  }, [isResult]);

  // Leaving the form (or unmounting) drops transient UI state so a later
  // reopen starts clean; the DRAFT itself lives in the context and survives.
  useEffect(() => {
    if (!isOpen) {
      copyGenRef.current += 1; // invalidate pending clipboard settles
      setPendingItem(null);
      setNoContentConfirm(false);
      setCopyStatus("idle");
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      copyGenRef.current += 1; // a settle after unmount must not set state
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    },
    []
  );

  const copyLbo = async () => {
    // "Kopiran" is only claimed once the write RESOLVES: clipboard access can
    // be denied (permission, insecure context, missing API), and the
    // pharmacist would otherwise paste nothing into the eApoteka portal while
    // holding a confirmation that says it worked. Trimmed to match what the
    // submit actually sends.
    const gen = ++copyGenRef.current;
    // A new attempt must not keep showing the PREVIOUS attempt's feedback
    // while its own write is pending — "LBO kopiran!" from value A reads as
    // confirmation of the just-clicked copy of value B.
    setCopyStatus("idle");
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
    let status: "copied" | "failed" = "copied";
    try {
      await navigator.clipboard.writeText(rfzoDraft.lbo.trim());
    } catch {
      status = "failed";
    }
    if (gen !== copyGenRef.current) {
      return; // superseded by a newer attempt or a modal close
    }
    setCopyStatus(status);
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
    }
    copyTimerRef.current = setTimeout(() => {
      setCopyStatus("idle");
    }, 5000);
  };

  const handleRequestClose = () => {
    if (isResult) {
      onCloseResult();
    } else {
      onClose();
    }
  };

  const graceBanner = (consultEnded || graceActive) && (
    <div className={styles.graceBanner} role="status">
      {isResult
        ? "Konsultacija je završena — RFZO rezervaciju možete kreirati još kratko."
        : "Konsultacija je završena — RFZO terapiju možete poslati još kratko."}
    </div>
  );

  const renderForm = () => (
    <>
      {graceBanner}
      {emailMissing && (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorText}>
            Korisnik nema e-mail nalog u apoteka.rs — RFZO terapija ne može da
            se kreira.
          </span>
        </div>
      )}
      {submitError && (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorText}>{submitError}</span>
          <button
            type="button"
            className={styles.errorDismiss}
            onClick={onDismissError}
            aria-label="Zatvori poruku o grešci"
          >
            Zatvori
          </button>
        </div>
      )}

      <div className={styles.body} ref={bodyRef}>
        <section className={styles.section} aria-label="LBO i JMBG korisnika">
          <h3 className={styles.sectionTitle}>LBO i JMBG korisnika</h3>
          <p className={styles.sectionNote}>
            Unesite LBO ili JMBG korisnika (bar jedno je obavezno).
          </p>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={lboId}>
              LBO
            </label>
            <input
              id={lboId}
              className={styles.input}
              data-consult-field="lbo"
              type="text"
              value={rfzoDraft.lbo}
              onChange={(e) => updateRfzoDraft({ lbo: e.target.value })}
              aria-invalid={validationErrors.lbo ? true : undefined}
              aria-describedby={validationErrors.lbo ? lboErrId : undefined}
            />
            {validationErrors.lbo && (
              <span id={lboErrId} className={styles.fieldError} role="alert">
                {validationErrors.lbo}
              </span>
            )}
            {!prefillHadLbo && (
              <span className={styles.hint}>
                Korisnik nema sačuvan LBO. Unesite LBO ili JMBG ručno.
              </span>
            )}
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={jmbgId}>
              JMBG
            </label>
            <input
              id={jmbgId}
              className={styles.input}
              data-consult-field="jmbg"
              type="text"
              inputMode="numeric"
              maxLength={13}
              value={rfzoDraft.jmbg}
              onChange={(e) => updateRfzoDraft({ jmbg: e.target.value })}
              aria-invalid={validationErrors.jmbg ? true : undefined}
              aria-describedby={validationErrors.jmbg ? jmbgErrId : undefined}
            />
            {validationErrors.jmbg && (
              <span id={jmbgErrId} className={styles.fieldError} role="alert">
                {validationErrors.jmbg}
              </span>
            )}
          </div>
          {/* The eApoteka portal lookup keys on the LBO only, so a JMBG-only
              patient can never enable this action (ITAM-2716). */}
          <button
            type="button"
            className={styles.copyButton}
            onClick={copyLbo}
            disabled={!rfzoDraft.lbo.trim()}
          >
            Kopiraj LBO i na portalu eApoteka proveri dostupnost terapije
          </button>
        </section>

        <section
          className={styles.section}
          aria-label="Podaci korisnika za rezervaciju"
        >
          <h3 className={styles.sectionTitle}>Podaci korisnika za rezervaciju</h3>
          <p className={styles.sectionNote}>
            Ovi podaci se koriste na RFZO rezervaciji — u koraku rezervacije ne
            mogu da se menjaju.
          </p>
          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={firstNameId}>
                Ime
              </label>
              <input
                id={firstNameId}
                className={styles.input}
                data-consult-field="firstName"
                type="text"
                value={rfzoDraft.firstName}
                onChange={(e) =>
                  updateRfzoDraft({ firstName: e.target.value })
                }
                aria-invalid={validationErrors.firstName ? true : undefined}
                aria-describedby={
                  validationErrors.firstName ? firstNameErrId : undefined
                }
              />
              {validationErrors.firstName && (
                <span
                  id={firstNameErrId}
                  className={styles.fieldError}
                  role="alert"
                >
                  {validationErrors.firstName}
                </span>
              )}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={secondNameId}>
                Prezime
              </label>
              <input
                id={secondNameId}
                className={styles.input}
                data-consult-field="secondName"
                type="text"
                value={rfzoDraft.secondName}
                onChange={(e) =>
                  updateRfzoDraft({ secondName: e.target.value })
                }
                aria-invalid={validationErrors.secondName ? true : undefined}
                aria-describedby={
                  validationErrors.secondName ? secondNameErrId : undefined
                }
              />
              {validationErrors.secondName && (
                <span
                  id={secondNameErrId}
                  className={styles.fieldError}
                  role="alert"
                >
                  {validationErrors.secondName}
                </span>
              )}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={phoneId}>
                Broj telefona
              </label>
              <input
                id={phoneId}
                className={styles.input}
                type="tel"
                value={rfzoDraft.userPhone}
                onChange={(e) =>
                  updateRfzoDraft({ userPhone: e.target.value })
                }
              />
              <span className={styles.hint}>
                Ako se ne unese, koristi se telefon sa naloga korisnika.
              </span>
            </div>
          </div>
        </section>

        <section className={styles.section} aria-label="Artikli terapije">
          <h3 className={styles.sectionTitle}>Artikli terapije</h3>
          <div
            className={styles.itemsAnchor}
            data-consult-field="items"
            tabIndex={-1}
          >
            <MedicineSearchBar
              label="Dodaj artikal"
              onSelect={(item) => setPendingItem(item)}
            />
          </div>
          {validationErrors.items && (
            <span className={styles.fieldError} role="alert">
              {validationErrors.items}
            </span>
          )}
          {rfzoDraft.items.length > 0 && (
            <ul className={styles.itemList} aria-label="Uneti artikli">
              {rfzoDraft.items.map((line) => (
                <li key={line.itemId} className={styles.itemRow}>
                  <span className={styles.itemName}>{line.name}</span>
                  <span className={styles.itemMeta}>
                    {line.rfzoQuantity} kom · participacija{" "}
                    {formatAmount(line.rfzoParticipationPrice)}
                  </span>
                  <button
                    type="button"
                    className={styles.itemRemove}
                    onClick={() => removeRfzoItem(line.itemId)}
                    aria-label={`Ukloni ${line.name}`}
                  >
                    Ukloni
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.section} aria-label="Datum dospeća terapije">
          <h3 className={styles.sectionTitle}>Datum dospeća terapije</h3>
          <p className={styles.hint}>
            Današnji datum: terapija je dostupna odmah. Budući datum: korisnik
            dobija podsetnike, rezervacija se ne kreira.
          </p>
          <Calendar
            onChange={(val) => {
              if (Array.isArray(val)) {
                updateRfzoDraft({ willBeReady: new Date() });
              } else {
                updateRfzoDraft({ willBeReady: val });
              }
            }}
            value={rfzoDraft.willBeReady}
            minDate={new Date()}
            locale="sr"
            className={styles.calendarWrap}
          />
          {rfzoDraft.willBeReady && (
            <button
              type="button"
              className={styles.clearDateButton}
              onClick={() => updateRfzoDraft({ willBeReady: null })}
            >
              Poništi izbor datuma
            </button>
          )}
        </section>

        <section
          className={styles.section}
          aria-label="Poslednji mesec dospeća terapije"
        >
          <h3 className={styles.sectionTitle}>
            Poslednji mesec dospeća terapije
          </h3>
          <p className={styles.hint}>
            Obavezno ako je izabran datum dospeća; mesec ne može biti pre meseca
            dospeća.
          </p>
          <div
            className={styles.monthAnchor}
            data-consult-field="lastTherapyDate"
            tabIndex={-1}
          >
            <Calendar
              onClickMonth={(val) => {
                if (Array.isArray(val)) {
                  updateRfzoDraft({ lastTherapyDate: new Date() });
                } else {
                  // +12h dodges the TZ midnight rollback (a month tile's Date is
                  // local midnight — serialized naively it can land in the
                  // previous month) — the exact RFZOOrderTherapy fix.
                  const date = new Date(val);
                  date.setHours(date.getHours() + 12);
                  updateRfzoDraft({ lastTherapyDate: date });
                }
              }}
              view="year"
              defaultView="year"
              value={rfzoDraft.lastTherapyDate}
              minDate={rfzoDraft.willBeReady ?? new Date()}
              locale="sr"
              className={styles.calendarWrap}
            />
          </div>
          {rfzoDraft.lastTherapyDate && (
            // Without a clear action a misclicked month could never be undone
            // (the calendar only allows picking a different one).
            <button
              type="button"
              className={styles.clearDateButton}
              onClick={() => updateRfzoDraft({ lastTherapyDate: null })}
            >
              Poništi izbor meseca
            </button>
          )}
          {validationErrors.lastTherapyDate && (
            <span className={styles.fieldError} role="alert">
              {validationErrors.lastTherapyDate}
            </span>
          )}
        </section>

        <section className={styles.section} aria-label="Napomene">
          <h3 className={styles.sectionTitle}>Napomene</h3>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={noteMarketplaceId}>
              Napomena za apoteku
            </label>
            <textarea
              id={noteMarketplaceId}
              className={styles.textarea}
              rows={2}
              value={rfzoDraft.noteForMarketplace}
              onChange={(e) =>
                updateRfzoDraft({ noteForMarketplace: e.target.value })
              }
            />
            <span className={styles.hint}>Prikazuje se na rezervaciji.</span>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={noteUserId}>
              Poruka za korisnika
            </label>
            <textarea
              id={noteUserId}
              className={styles.textarea}
              rows={2}
              value={rfzoDraft.noteForUser}
              onChange={(e) => updateRfzoDraft({ noteForUser: e.target.value })}
            />
            <span className={styles.hint}>
              Prikazuje se korisniku uz podsetnike, ne na rezervaciji.
            </span>
          </div>
        </section>
      </div>

      {noContentConfirm ? (
        <footer className={styles.footer}>
          <p className={styles.noContentConfirmText} role="alert">
            Korisnik će biti obavešten da nema propisanu RFZO terapiju. Da li
            ste sigurni?
          </p>
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setNoContentConfirm(false)}
            >
              Odustani
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={isSubmitting || emailMissing}
              onClick={() => {
                setNoContentConfirm(false);
                onSubmit("NO_CONTENT");
              }}
            >
              Potvrdi
            </button>
          </div>
        </footer>
      ) : (
        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.tertiary}
            disabled={isSubmitting || emailMissing}
            onClick={() => setNoContentConfirm(true)}
          >
            Korisnik nema RFZO terapiju
          </button>
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.secondary}
              onClick={onClose}
            >
              Otkaži
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={isSubmitting || emailMissing}
              onClick={() => onSubmit("NORMAL")}
            >
              {isSubmitting ? (
                <span className={styles.submitSpinner}>
                  <ScaleLoader color="#ffffff" height={14} width={3} />
                </span>
              ) : (
                "Kreiraj terapiju"
              )}
            </button>
          </div>
        </footer>
      )}
    </>
  );

  const renderResult = () => {
    if (!rfzoResult) {
      return null;
    }
    const orderable = rfzoResult.orderable === true;
    const items = rfzoResult.items ?? [];
    const someInStock = someRfzoItemInStock(items);
    const noContent = rfzoResult.status === "PROCESSED_WITHOUT_CONTENT";

    if (!orderable) {
      // Terminal outcomes: VIEW-ONLY confirmation (no rerun — Q6). Reopening
      // via the prompt ("Pregled RFZO terapije") shows this same summary.
      return (
        <div className={styles.resultBody} role="status">
          {noContent ? (
            <>
              <p className={styles.resultHeadline}>
                Zahtev je obrađen bez terapije.
              </p>
              <p className={styles.resultText}>
                Korisnik je obavešten da nema propisanu RFZO terapiju.
              </p>
              {rfzoResultMode === "NORMAL" && rfzoDraft.items.length > 0 && (
                // Only meaningful for a NORMAL submit: the explicit "korisnik
                // nema terapiju" action never sends the items, so claiming they
                // could not be matched would be false.
                <p className={styles.warningText}>
                  Nijedan uneti artikal nije mogao da se poveže sa artiklima u
                  vašoj apoteci, pa je zahtev obrađen bez sadržaja.
                </p>
              )}
            </>
          ) : (
            <>
              <p className={styles.resultHeadline}>
                RFZO terapija je evidentirana.
              </p>
              <p className={styles.resultText}>
                Podsetnici su kreirani i korisnik je obavešten o terapiji.
                Rezervacija se ne kreira jer terapija još nije dospela.
              </p>
            </>
          )}
          <div className={styles.footerActions}>
            <button
              type="button"
              ref={resultCloseRef}
              className={styles.primary}
              onClick={onCloseResult}
            >
              Zatvori
            </button>
          </div>
        </div>
      );
    }

    const missingNames = rfzoDraft.items
      .filter(
        (line) => !items.some((it) => it.itemResponse?.id === line.itemId)
      )
      .map((line) => line.name);

    return (
      <>
        {graceBanner}
        <div className={styles.resultBody}>
          <p className={styles.resultHeadline} role="status">
            RFZO terapija je kreirana.
          </p>
          {items.length > 0 && (
            <ul className={styles.itemList} aria-label="Artikli terapije">
              {items.map((it, idx) => (
                <li key={it.itemResponse?.id ?? idx} className={styles.itemRow}>
                  <span className={styles.itemName}>
                    {it.itemResponse?.name ?? "Artikal"}
                    {/* Same predicate as the order gate, so a line can never
                        look available while the dead-end message claims
                        otherwise. */}
                    {!isRfzoItemInStock(it) && (
                      <span className={styles.stockBadge}>nije na stanju</span>
                    )}
                  </span>
                  <span className={styles.itemMeta}>
                    {/* rfzoUnitParticipationPrice is the PER-UNIT amount the
                        pharmacist entered; the response's rfzoParticipationPrice
                        is the line total (unit x quantity), which under this
                        label would read as a repriced unit cost. */}
                    {it.rfzoQuantity ?? "—"} kom · participacija{" "}
                    {it.rfzoUnitParticipationPrice != null
                      ? formatAmount(it.rfzoUnitParticipationPrice)
                      : "—"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className={styles.totalLine}>
            Ukupna participacija:{" "}
            {formatAmount(rfzoResult.totalParticipationPrice ?? 0)}
          </p>
          {missingNames.length > 0 && (
            <p className={styles.warningText} role="alert">
              Sledeći artikli nisu dostupni u vašoj apoteci i nisu uneti u
              terapiju: {missingNames.join(", ")}.
            </p>
          )}
          {!someInStock && (
            // The all-understock dead end (plan Section 9): the therapy is
            // persisted, the regular order is blocked — support-facing copy.
            <p className={styles.warningText} role="alert">
              Nijedan artikal iz terapije trenutno nije na stanju u dovoljnoj
              količini — RFZO rezervacija ne može da se kreira. Terapija je već
              evidentirana za korisnika, pa obična rezervacija za ovu
              konsultaciju nije moguća; za poništavanje terapije kontaktirajte
              podršku.
            </p>
          )}
          <div className={styles.footerActions}>
            <button
              type="button"
              ref={resultCloseRef}
              className={styles.secondary}
              onClick={onCloseResult}
            >
              Zatvori
            </button>
            {someInStock && (
              <button
                type="button"
                ref={resultPrimaryRef}
                className={styles.primary}
                onClick={onStartOrder}
              >
                Kreiraj rezervaciju
              </button>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <AccessibleDialog
        isOpen={isOpen}
        onRequestClose={handleRequestClose}
        titleId={TITLE_ID}
        maxWidth={760}
        className={styles.dialogInner}
      >
        <div className={styles.container}>
          <header className={styles.header}>
            <h2 id={TITLE_ID} className={styles.title}>
              RFZO terapija — {callerName}
            </h2>
            {/* The account the therapy is attached to: reminders and every
                notification go to this e-mail, so the pharmacist must be able
                to see it before submitting (a shared family account is not
                always the person on the call). */}
            <p className={styles.callerMeta}>
              {[
                prefill?.user.phoneNumber?.trim(),
                prefill?.user.username?.trim(),
              ]
                .filter(Boolean)
                .join(" · ") || "Nema kontakt podataka"}
            </p>
          </header>
          {/* Rendered INSIDE the dialog: a page-level toast would sit behind
              this modal's overlay (z-index 80 vs 1000) and inside the
              aria-hidden app root, so the copy confirmation would be both
              invisible and unannounced. */}
          {copyStatus !== "idle" && (
            <NotificationMessage
              className={
                copyStatus === "failed"
                  ? `${styles.copyToast} ${styles.copyToastFailed}`
                  : styles.copyToast
              }
              role={copyStatus === "failed" ? "alert" : "status"}
            >
              {copyStatus === "copied"
                ? "LBO kopiran!"
                : "Kopiranje LBO nije uspelo — kopirajte ga ručno."}
            </NotificationMessage>
          )}
          {isResult ? renderResult() : renderForm()}
        </div>
      </AccessibleDialog>

      {pendingItem && (
        <ConsultRfzoItemDialog
          item={pendingItem}
          onAdd={addRfzoItem}
          onClose={() => setPendingItem(null)}
        />
      )}
    </>
  );
};

export default ConsultRfzoFormModal;
